import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Form, Input, Button, Card, Select, DatePicker, message, Radio} from "antd";
import {LeftOutlined} from "@ant-design/icons";
import {Agent} from "../../types";
import api from "../../api";
import dayjs from "dayjs";
import Title from "antd/es/typography/Title";

const {Option} = Select;

const NewTripPage: React.FC = () => {
        const navigate = useNavigate();
        const [customers, setCustomers] = useState([]);
        const [agents, setAgents] = useState<Agent[]>([]);
        const [buyinMethod, setBuyinMethod] = useState<string | null>(null);
        const [exchangeCurrency, setExchangeCurrency] = useState<string>("None");
        const [exchangeAmount, setExchangeAmount] = useState<number | null>(null);
        const [exchangeRate, setExchangeRate] = useState<number | null>(null);
        const [buyinAmount, setBuyinAmount] = useState<number | null>(null);
        const [checkinDate, setCheckinDate] = useState<dayjs.Dayjs | null>(null);
        const [exchangeRates, setExchangeRates] = useState<any>({});
        const [ratesMissing, setRatesMissing] = useState(false);
        const [form] = Form.useForm();

        useEffect(() => {
                api.get("/daia/customers/", {
                    params: {all: "true"},
                }).then((res) => {
                    const customersArray = Array.isArray(res.data) ? res.data : res.data.results || [];
                    setCustomers(customersArray);
                }).catch((error) => {
                    console.error("Failed to fetch all customers:", error);
                    message.error("Failed to load all customers");
                });
                api.get("/daia/agents/", {
                    params: {all: "true"}
                }).then((res) => {
                    const agentsArray = Array.isArray(res.data) ? res.data : res.data.results || [];
                    setAgents(agentsArray);
                }).catch((error) => {
                    console.error("Failed to fetch all agents:", error);
                    message.error("Failed to load all agents");
                });
            }, []
        );

        useEffect(() => {
            const fetchRates = async () => {
                try {
                    const res = await api.get("/dolphin/rates/");
                    if (res.data.length === 0) {
                        setRatesMissing(true);
                    } else {
                        setExchangeRates(res.data[0]);
                        setRatesMissing(false);
                    }
                } catch (err) {
                    setRatesMissing(true); // Treat error same as missing rates
                }
            };
            fetchRates();
        }, []);

        useEffect(() => {
            if (exchangeCurrency !== "None" && exchangeAmount && exchangeRate) {
                const calculatedBuyin = exchangeAmount * exchangeRate;
                setBuyinAmount(calculatedBuyin);
                form.setFieldsValue({buyin_amount: calculatedBuyin.toLocaleString()});
            } else {
                setBuyinAmount(null);
                form.setFieldsValue({buyin_amount: null});
            }
        }, [exchangeAmount, exchangeRate, exchangeCurrency, form]);

        const handleFinish = async (values: any) => {
            try {
                const rawBuyin =
                    exchangeCurrency === "None"
                        ? parseFloat(values.buyin_amount.replace(/,/g, ""))
                        : buyinAmount || 0;

                const finalBuyin = Math.round(rawBuyin);

                const payload = {
                    customer: values.customer,
                    buy_in_method: values.buyin_method,
                    exchange_currency: exchangeCurrency === "None" ? null : exchangeCurrency,
                    exchange_rate: exchangeRate || null,
                    exchange_amount: exchangeAmount || null,
                    locked_exchange_rate: values.locked_exchange_rate === "yes",
                    buyin_amount: finalBuyin,
                    agent: values.agent ? values.agent : null,
                    agent_structure: values.agent_structure || "None",
                    agent_id: values.agent === null ? null : values.agent,
                    checkin_date: values.checkin_date.format("YYYY-MM-DD"),
                    checkout_date: values.checkout_date?.format("YYYY-MM-DD") || null,
                    bonus_structure: values.bonus_structure,
                    final_balance: null,
                    memo: values.memo
                };

                await api.post("/daia/trips/", payload);

                message.success("Trip successfully created!");
                navigate("/daia/trips/");
            } catch (error: any) {
                console.error("Error creating trip:", error);

                if (error.response) {
                    console.error("Response Data:", error.response.data);
                    console.error("Response Status:", error.response.status);
                    console.error("Response Headers:", error.response.headers);

                    message.error(
                        `Failed to create trip: ${JSON.stringify(error.response.data) || "Please check the input values."}`
                    );
                } else {
                    message.error("Failed to create trip. Server is unreachable.");
                }
            }
        };

        useEffect(() => {
            if (exchangeCurrency && exchangeCurrency !== "None") {
                let rate = 0;

                if (exchangeCurrency === "USDT-TRC") {
                    rate = exchangeRates.usdt_trc_buy || 0;
                } else if (exchangeCurrency === "USDT-ERC") {
                    rate = exchangeRates.usdt_erc_buy || 0;
                } else {
                    const field = `${exchangeCurrency.toLowerCase()}_buy`;
                    rate = exchangeRates[field] || 0;
                }

                setExchangeRate(rate);

                if (exchangeAmount) {
                    const calculatedBuyin = exchangeAmount * rate;
                    setBuyinAmount(calculatedBuyin);
                    form.setFieldsValue({buyin_amount: calculatedBuyin.toLocaleString()});
                }
            } else {
                setExchangeRate(null);
            }
        }, [exchangeCurrency, exchangeAmount, exchangeRates, form]);


        const getExchangeOptions = () => {
            if (buyinMethod === "Cash") {
                return ["None", "YEN", "HKD", "USD"];
            } else if (buyinMethod === "Crypto") {
                return ["None", "USDT-TRC", "USDT-ERC"];
            }
            return ["None"];
        };

        return (
            <Card
                title={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 12,
                        }}
                    >
                        <Title level={2} style={{margin: 0}}>New Trip</Title>
                    </div>
                }
                style={{maxWidth: 700, margin: "24px auto", padding: 24}}
            >
                <div style={{marginBottom: 16}}>
                    <Button icon={<LeftOutlined/>} onClick={() => navigate(-1)}>
                        Back
                    </Button>
                </div>

                {ratesMissing ? (
                    <div style={{padding: 24, color: "#a00", fontSize: 16}}>
                        ⚠️ Before you can create a trip, you must enter at least one set of exchange rates in the Dolphin
                        section.
                    </div>
                ) : (
                    <Form form={form} layout="vertical" onFinish={handleFinish}>
                        <Form.Item name="customer" label="Customer" rules={[{required: true}]}>
                            <Select
                                showSearch
                                placeholder="Select a customer"
                                options={customers.map((customer: any) => ({
                                    label: `${customer.first_name} ${customer.last_name} (MMID: ${customer.mmid})`,
                                    value: customer.id,
                                    agentId: customer.agent,
                                    agentProgram: customer.agent_name?.match(/\(([^)]+)\)/)?.[1] || "None",
                                }))}
                                filterOption={(input, option) =>
                                    (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={(value, option) => {
                                    const selected = option as any;
                                    form.setFieldsValue({
                                        customer: value,
                                        agent: selected.agentId || null,
                                        agent_structure: selected.agentProgram || "None",
                                    });
                                }}
                            />
                        </Form.Item>

                        <Form.Item name="buyin_method" label="Buy-in Method" rules={[{required: true}]}>
                            <Select
                                placeholder="Select method"
                                onChange={(value) => {
                                    setBuyinMethod(value);
                                    setExchangeCurrency("None");
                                    setExchangeRate(null);
                                    setExchangeAmount(null);
                                    setBuyinAmount(null);
                                    form.setFieldsValue({
                                        exchange_currency: "None",
                                        exchange_amount: null,
                                        exchange_rate: null,
                                        buyin_amount: null,
                                        locked_exchange_rate: null,
                                    });
                                }}
                            >
                                <Option value="Cash">Cash</Option>
                                <Option value="Crypto">Crypto</Option>
                                <Option value="Credit">Credit</Option>
                            </Select>
                        </Form.Item>

                        {buyinMethod && (
                            <Form.Item name="exchange_currency" label="Exchange Currency">
                                <Select
                                    placeholder="Select currency"
                                    value={exchangeCurrency}
                                    onChange={(value) => setExchangeCurrency(value)}
                                >
                                    {getExchangeOptions().map((currency) => (
                                        <Option key={currency} value={currency}>
                                            {currency}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}

                        {exchangeCurrency !== "None" && (
                            <>
                                <Form.Item label="Exchange Amount">
                                    <Input
                                        type="text"
                                        placeholder="Enter amount in foreign currency"
                                        value={exchangeAmount !== null ? exchangeAmount.toLocaleString() : ""}
                                        onChange={(e) =>
                                            setExchangeAmount(parseFloat(e.target.value.replace(/,/g, "")) || null)
                                        }
                                    />
                                </Form.Item>

                                <Form.Item label="Exchange Rate">
                                    <Input value={exchangeRate !== null ? exchangeRate : "Loading..."} disabled/>
                                </Form.Item>

                                <Form.Item name="locked_exchange_rate" label="Locked Rate">
                                    <Radio.Group>
                                        <Radio value="yes">Yes</Radio>
                                        <Radio value="no">No</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </>
                        )}

                        <Form.Item name="buyin_amount" label="Buy-in Amount" rules={[{required: true}]}>
                            <Input
                                type="text"
                                prefix="₩"
                                disabled={exchangeCurrency !== "None"}
                                value={buyinAmount !== null ? Number(buyinAmount).toLocaleString() : ""}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/,/g, "");
                                    const numeric = parseInt(raw, 10);
                                    if (!isNaN(numeric)) {
                                        setBuyinAmount(numeric);
                                        form.setFieldsValue({buyin_amount: numeric.toLocaleString()});
                                    } else {
                                        setBuyinAmount(null);
                                        form.setFieldsValue({buyin_amount: ""});
                                    }
                                }}
                            />
                        </Form.Item>

                        <Form.Item name="agent" label="Agent">
                            <Select
                                placeholder="Select an agent"
                                onChange={(value) => {
                                    if (value === null) {
                                        form.setFieldsValue({
                                            agent: null,
                                            agent_structure: "None",
                                        });
                                    } else {
                                        const selectedAgent = agents.find((a) => a.id === value);
                                        if (selectedAgent) {
                                            form.setFieldsValue({
                                                agent: selectedAgent.id,
                                                agent_structure: selectedAgent.program,
                                            });
                                        }
                                    }
                                }}
                            >
                                {agents.map((agent: any) => (
                                    <Option key={agent.id} value={agent.id}>
                                        {agent.first_name} {agent.last_name} ({agent.program})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item name="checkin_date" label="Check-in Date" rules={[{required: true}]}>
                            <DatePicker
                                format="YYYY-MM-DD"
                                style={{width: "100%"}}
                                // disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
                                onChange={(date) => setCheckinDate(date)}
                            />
                        </Form.Item>

                        <Form.Item name="checkout_date" label="Check-out Date">
                            <DatePicker
                                format="YYYY-MM-DD"
                                style={{width: "100%"}}
                                // disabledDate={(current) =>
                                //     checkinDate ? current && !current.isAfter(checkinDate, "day") : false
                                // }
                            />
                        </Form.Item>

                        <Form.Item name="bonus_structure" label="Customer Bonus Structure" rules={[{required: true}]}>
                            <Select>
                                <Option value="ROL">ROL</Option>
                                <Option value="Rolling">Rolling</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="Memo" name="memo"><Input.TextArea rows={4}/></Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" block>
                                Create Trip
                            </Button>
                        </Form.Item>
                    </Form>
                )}
            </Card>

        );
    }
;

export default NewTripPage;
